import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema, PaginatedResponseSchema } from './common';

/**
 * Alumno = persona única del Área de Formación Permanente y Docencia,
 * identificada por la versión normalizada de su cédula/pasaporte.
 *
 * Una misma persona puede participar en N cohortes; cada participación
 * genera un `participante` que apunta a este `alumno` vía `alumno_id`.
 *
 * Las columnas `*_normalizado*` se mantienen en minúsculas, sin tildes y sin
 * separadores redundantes; son aptas para JOINs e índices secundarios.
 */
export const AlumnoSchema = z.object({
  id: EntityIdSchema,
  cedula_original: z.string().min(1),
  cedula_normalizada: z.string().min(1),
  nombre_original: z.string().min(1),
  apellido_original: z.string().min(1),
  nombre_normalizado: z.string().min(1),
  apellido_normalizado: z.string().min(1),
  email: z.string().email().nullable().optional(),
  telefono: z.string().nullable().optional(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
});
export type Alumno = z.infer<typeof AlumnoSchema>;

/**
 * Input para crear / reusar un alumno (idempotente por cédula normalizada).
 * La normalización corre en backend (lib/normalizar.ts).
 */
export const AlumnoUpsertInputSchema = z.object({
  cedula: z.string().min(1),
  nombre: z.string().min(1),
  apellido: z.string().min(1),
  email: z.string().email().optional(),
  telefono: z.string().optional(),
});
export type AlumnoUpsertInput = z.infer<typeof AlumnoUpsertInputSchema>;

/** Respuesta de `findOrCreatePorCedula` — incluye flag de creación. */
export const AlumnoUpsertResultSchema = z.object({
  alumno: AlumnoSchema,
  created: z.boolean(),
});
export type AlumnoUpsertResult = z.infer<typeof AlumnoUpsertResultSchema>;

/** Respuesta de GET /api/v1/alumnos (paginado). */
export const AlumnoListResponseSchema = PaginatedResponseSchema(AlumnoSchema);
export type AlumnoListResponse = z.infer<typeof AlumnoListResponseSchema>;
